import { LIMITES, ROTULO_DO_MOTOR, type Motor } from '@remoto/protocolo';

/**
 * A herança em texto de uma troca de motor: as últimas mensagens da conversa
 * antiga, emolduradas pelo SERVIDOR, prontas para virar `primeiraMensagem` da
 * sessão nova no outro motor.
 *
 * O texto das mensagens vem de `eventos_sessao` via `textoDoEvento` — a fonte
 * JÁ redigida de `isSecret` —, nunca do transcript cru da máquina. Este módulo
 * não extrai nada: ele só emoldura o que o chamador projetou.
 *
 * **Corte declarado, nunca silencioso.** A moldura diz quantas mensagens
 * entraram e quantas existem; quando a mais recente sozinha não cabe, ela
 * entra cortada pelo INÍCIO (o fim é o presente) e a moldura declara isso.
 */

/**
 * Orçamento padrão para os blocos de mensagem, com folga sob o teto de
 * `primeiraMensagem` (`LIMITES.textoMensagem`, 65 536): a folga é da moldura.
 * Contado por `String.length`, a mesma unidade do `z.string().max`.
 */
export const ORCAMENTO_HERANCA = 60_000;

export interface MensagemDaHeranca {
  papel: 'user' | 'assistant';
  texto: string;
}

export interface OpcoesDaHeranca {
  /** O título da conversa antiga (`tituloDaSessao`). */
  titulo: string;
  /** O motor em que a conversa RODAVA — o rótulo dele entra na moldura. */
  motorOrigem: Motor;
  /** Ordem CRONOLÓGICA (mais antiga primeiro), texto já não-vazio. */
  mensagens: MensagemDaHeranca[];
  /** Quantas mensagens de conversa a sessão TEM registradas — o M da moldura. */
  totalRegistrado: number;
  /** Orçamento dos blocos de mensagem; padrão `ORCAMENTO_HERANCA`. */
  orcamento?: number;
}

const TETO = LIMITES.textoMensagem;
const SEPARADOR = '\n\n';
const ROTULO_PAPEL: Record<MensagemDaHeranca['papel'], string> = {
  user: '[pessoa]',
  assistant: '[assistente]',
};

interface Encaixe {
  blocos: string[];
  incluidas: number;
  mensagemCortada: boolean;
}

/**
 * Preenche de TRÁS para frente (mais recentes primeiro), por mensagem
 * inteira. Se nem a mais recente coube, ela entra cortada pelo início — o fim
 * é o presente, e é o fim que a sessão nova precisa para continuar.
 */
function encaixar(mensagens: readonly MensagemDaHeranca[], orcamento: number): Encaixe {
  const blocos: string[] = [];
  let usado = 0;
  for (let i = mensagens.length - 1; i >= 0; i -= 1) {
    const m = mensagens[i]!;
    const bloco = `${ROTULO_PAPEL[m.papel]}: ${m.texto}`;
    const custo = bloco.length + (blocos.length > 0 ? SEPARADOR.length : 0);
    if (usado + custo > orcamento) break;
    blocos.unshift(bloco);
    usado += custo;
  }
  if (blocos.length > 0 || mensagens.length === 0) {
    return { blocos, incluidas: blocos.length, mensagemCortada: false };
  }

  // A mais recente sozinha estoura o orçamento: entra cortada pelo início.
  const ultima = mensagens[mensagens.length - 1]!;
  const prefixo = `${ROTULO_PAPEL[ultima.papel]}: […] `;
  const espaco = orcamento - prefixo.length;
  if (espaco <= 0) return { blocos: [], incluidas: 0, mensagemCortada: false };
  return {
    blocos: [prefixo + ultima.texto.slice(-espaco)],
    incluidas: 1,
    mensagemCortada: true,
  };
}

function moldura(
  opcoes: OpcoesDaHeranca,
  incluidas: number,
  mensagemCortada: boolean,
): string {
  const linhas = [
    `Esta conversa continua a sessão «${opcoes.titulo}», que rodava no ` +
      `${ROTULO_DO_MOTOR[opcoes.motorOrigem]} nesta mesma pasta.`,
  ];
  if (incluidas === 0) {
    // Lista vazia não é erro: a sessão que nunca falou continua com a moldura
    // honesta dizendo que não havia o que trazer.
    linhas.push(
      opcoes.totalRegistrado === 0
        ? 'Ela ainda não tinha nenhuma mensagem registrada: o histórico começa aqui.'
        : `Nenhuma das ${opcoes.totalRegistrado} mensagens registradas coube aqui ` +
            '(o histórico completo está no painel).',
    );
  } else {
    linhas.push(
      `Abaixo, as últimas ${incluidas} das ${opcoes.totalRegistrado} mensagens registradas` +
        (incluidas < opcoes.totalRegistrado ? ' (o restante está no painel).' : '.'),
    );
  }
  if (mensagemCortada) {
    linhas.push(
      'A mensagem mais recente não coube inteira: ela entra cortada pelo início, marcada com […].',
    );
  }
  linhas.push(
    'Ferramentas executadas lá não aparecem aqui — confira o estado real dos ' +
      'arquivos antes de assumir qualquer coisa.',
  );
  return linhas.join('\n');
}

/**
 * Monta a herança. O retorno NUNCA passa de `LIMITES.textoMensagem` (65 536),
 * moldura inclusa — mesmo que o chamador passe um orçamento encostado no teto:
 * o laço reduz o orçamento das mensagens até a soma caber.
 */
export function montarHeranca(opcoes: OpcoesDaHeranca): string {
  let orcamento = Math.min(opcoes.orcamento ?? ORCAMENTO_HERANCA, TETO);
  for (;;) {
    const encaixe = encaixar(opcoes.mensagens, Math.max(orcamento, 0));
    const cabecalho = moldura(opcoes, encaixe.incluidas, encaixe.mensagemCortada);
    const texto =
      encaixe.blocos.length > 0
        ? cabecalho + SEPARADOR + encaixe.blocos.join(SEPARADOR)
        : cabecalho;
    if (texto.length <= TETO) return texto;
    /*
      Só se chega aqui com orçamento encostado no teto (o padrão de 60 000
      deixa mais de 5 000 de folga para a moldura). A redução é ESTRITA para o
      laço terminar mesmo quando a moldura cresce entre as voltas — a linha da
      mensagem cortada, por exemplo, entra numa volta em que antes não estava.
    */
    orcamento = Math.min(orcamento - 1, TETO - cabecalho.length - SEPARADOR.length);
  }
}
